﻿import React, { useState, useEffect } from 'react';
import { AnalysisDatum } from '../../models/analysis-datum';
import { ModalMessageType, MessageBoxReplyType, Entities } from '../../models/enums';
import { CrudFunctions } from '../../utilities/crud-functions';
import AnalysisData from '../AnalysisData';
import ItemBasicInformationDialog from '../ItemBasicInformationDialog';
import { ItemBasicInformation } from '../../models/item-basic-information';
import AuditHistoryComponent from '../AuditHistoryComponent';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { RenderingFunctions } from '../../utilities/rendering-functions';
import { MessageBoxReply } from '../../models/message-box-reply';

import DataGrid, {
    Column,
    Paging,
    Button as ColumnButton,
    Toolbar,
    Item as ToolbarItem,
    LoadPanel,
    Editing
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

//const ANALYSIS_DATUM_ID_COLUMN_IDX = 0;
//const EDIT_TYPE_COLUMN_ID = "editType";
//const RAW_MATERIAL_LOOKUP_COLUMN_ID = "rawMaterialLookup";
const UNSAVED_CHANGES_WARNING = 100;
const CREATE_NEW_ANALYSIS_DATA_WARNING = 110;

let key = 1;

export interface Props {
    benchSheetId?: string;
    spotCheckBenchSheetId?: string;
    batchId?: string;
    isReadOnly: boolean;
    datagridHeight?: string;
    title?: string;
    hideElutionColumn?: boolean;
    hideRetentionTimeColumn?: boolean;
}

export default function AnalysisDataEditor(props: Props) {
    const [analysisData, setAnalysisData] =
        useState<AnalysisDatum[]>();

    const [selectedAnalysisDatum, setSelectedAnalysisDatum] =
        useState<AnalysisDatum>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.benchSheetId]);

    key++;

    return (
        <div>
            <div id="analysis-data-datagrid-container" className="pw-datagrid">
                <DataGrid
                    height={props.datagridHeight ? props.datagridHeight : "100%"}
                    keyExpr="analysisDatumId"
                    dataSource={analysisData ?
                        analysisData : []}
                    onSelectionChanged={analysisDataSelectionChanged}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true} >
                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />

                    <Column type="buttons" width='8rem' >
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={showAuditInformationDialog} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onAnalysisDataInformationButtonClicked} />
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />

                    </Column>

                    {!props.hideElutionColumn &&
                        <Column dataField='elution' dataType="number"
                            alignment='center' width='8rem' />
                    }

                    <Column dataField='partNumber'

                        allowEditing={false} width='24rem' />

                    <Column dataField='productState'
                        cellRender={analysisDatumProductStateRender}
                        allowEditing={false} width='8rem' />

                    <Column dataField='result' dataType="number" format={decimalFormat} width='9rem' />
                    <Column dataField='uncertainty' dataType="number" format={decimalFormat} width='9rem' />

                    {!props.hideRetentionTimeColumn &&
                        <Column dataField='retentionTime' dataType="number" format={decimalFormat} width='9rem' />
                    }

                    <Column />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Analysis Data</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem location="after">
                            <Button hint="duplicate row" icon="inserttable" disabled={props.isReadOnly}
                                width="1.8rem" onClick={onGenerateAnalysisDataClick} />
                        </ToolbarItem>

                        <ToolbarItem location="after">
                            <Button hint="duplicate row" icon="insertrowbelow" disabled={props.isReadOnly}
                                width="1.8rem" onClick={onDuplicateRowClick} />
                        </ToolbarItem>

                        <ToolbarItem location="after" name="addNewRow" />
                    </Toolbar>

                    <Editing
                        mode="row"
                        useIcons={true}
                        allowAdding={true}
                        allowUpdating={true}
                        allowDeleting={true} >
                    </Editing>

                </DataGrid>

            </div>

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={Entities.AnalysisData}
                        key={`AuditHistory${key}`}
                        childData={false}
                        rowPointer={selectedAnalysisDatum && selectedAnalysisDatum.analysisDatumId ?
                            selectedAnalysisDatum.analysisDatumId : undefined} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata}
                    onReply={onGlobalDialogReply} onClose={onGlobalDialogClose} />
            }

        </div>
    );

    function onAnalysisDataInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            setSelectedAnalysisDatum(e.row.data);
            showReadingInformationDialog(e.row.data);
        }
    }

    function analysisDataSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedAnalysisDatum(e.selectedRowsData[0]);
        }
    }

    function onDuplicateRowClick(e: any) {
        console.log("onDuplicateRowClick");
        console.log(e);


    }

    function onGenerateAnalysisDataClick(e: any) {

        warnCreateAnalysisData();

    }



    /*
    async function onCloseAnalysisDataEditor() {
        //refresh data:
        await getAnalysisData();
        hideAnalysisDataDialog();
    }
    */

    // CRUD functions

    async function getAnalysisData() {
        if (props.benchSheetId)
            await CrudFunctions.getData(`benchsheets/analysisdatabybenchsheetid/${props.benchSheetId}`)
                .then(data => {
                    setAnalysisData(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Analysis Data");
                });
    }

    //TODO: change to just create data but do not save:
    async function createAnalysisData() {
        await CrudFunctions.postData(`benchsheets/createanalysisdataforbenchsheet/${props.benchSheetId}/${props.batchId}`, null)
            .then(() => {
                getAnalysisData();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Creating Analysis Data");
            });
    }


    async function updateAnalysisData(preparedAnalysisData: AnalysisDatum[]) {
        if (preparedAnalysisData) {
            await CrudFunctions.putData("benchsheets/analysisdata", preparedAnalysisData)
                .then(() => {
                    getAnalysisData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Analysis Data");
                });
        }
    }


    async function getAllData() {
        await getAnalysisData();
    }




    //render

    function analysisDatumProductStateRender(e: any) {
        let analysisDatum = e.data as AnalysisDatum;

        if (analysisDatum) {
            let cssClass = RenderingFunctions.returnProductStateColor(analysisDatum.productState);

            return <div className={cssClass}>{RenderingFunctions.returnProductStateName(analysisDatum.productState)}</div>;
        }
        else
            return <div></div>;
    }

    // show/hide


    function showReadingInformationDialog(analysisDatum: AnalysisDatum) {
        if (analysisDatum) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: analysisDatum.analysisDatumId,
                createTimestamp: analysisDatum.createTimestamp,
                lastModifiedTimestamp: analysisDatum.modifyTimestamp,
                createUser: analysisDatum.createUser,
                modifyUser: analysisDatum.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showAuditInformationDialog() {
        setAuditInformationDialogVisible(true);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function warnCreateAnalysisData() {
        //we do not want to overwrite any analysis data
        if (analysisData && analysisData.length > 0) {
            showPromptMessage(ModalMessageType.PromptYesNo,
                "Analysis Data Change",
                "This action will delete any existing analysis data, and create a new set. Do you want to proceed?",
                CREATE_NEW_ANALYSIS_DATA_WARNING);

            return;
        }
        else
            createAnalysisData();
    }

    function showPromptMessage(mode: ModalMessageType, title: string, message: string, code: number) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message,
            code: code
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function onGlobalDialogReply(reply: MessageBoxReply) {
        if (reply.code === CREATE_NEW_ANALYSIS_DATA_WARNING) {
            clearGlobalDialog();

            if (reply.reply !== MessageBoxReplyType.Yes)
                return;

            createAnalysisData();
        }
        if (reply.code === UNSAVED_CHANGES_WARNING) {
            clearGlobalDialog();

            if (reply.reply !== MessageBoxReplyType.Yes)
                return;
        }
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function onGlobalDialogClose() {
        clearGlobalDialog();
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }
}


